// A place to send auth events that would otherwise disappear.
//
// `getAccessTokenSilently` failing is not always a real end of session, but the
// SDK treats it as one: its `finally` dispatches GET_ACCESS_TOKEN_COMPLETE with
// `getUser()` returning undefined, so `isAuthenticated` flips to false with no
// `error` set and nothing to report. On top of that, each app's Sentry
// `beforeSend` deliberately drops session-expired *exceptions*, because a
// session that ended is not a crash. Between them, an app can bounce a member
// through Auth0 repeatedly and leave no trace of the OAuth code that caused it.
//
// This package must not depend on Sentry, so the sink is injected: each app
// wires `setAuthTelemetry` in its entry point. A reported event is a *message*,
// not an exception, so it survives the `beforeSend` filter.

export interface AuthEvent {
  /** Short, stable name — this is what shows up in the issue list. */
  name: string
  /** Never a token, an email or anything else that identifies the member. */
  data: Record<string, unknown>
  /** How loud it is in Sentry. Omitted means `warning`. */
  level?: 'info' | 'warning'
}

let sink: ((event: AuthEvent) => void) | null = null

const CACHE_PREFIX = '@@auth0spajs@@::'
const USER_SUFFIX = '::@@user@@'

export interface TokenCacheFacts {
  /** False when localStorage itself could not be read. */
  storageOk: boolean
  /** Whether the SDK's id-token entry (what `isAuthenticated` is built from) exists. */
  hasUser: boolean
  /** Access-token entries the SDK has cached (the id-token entry is excluded). */
  entries: Array<{
    requestedScope: string | null
    grantedScope: string | null
    hasAccessToken: boolean
    hasRefreshToken: boolean
    secondsToExpiry: number | null
  }>
}

/**
 * What the Auth0 SDK's localStorage cache holds, minus anything secret.
 *
 * A `login_required` straight after a successful code exchange means the token
 * just written was not served back, and the retry had nothing to renew with.
 * Which of those it was — no entry, an entry already inside the SDK's 60s
 * expiry margin, or an entry with no refresh token — is only visible here.
 * Token values are reduced to booleans; they never leave this function.
 */
export function describeTokenCache(): TokenCacheFacts {
  try {
    const now = Math.floor(Date.now() / 1000)
    const entries: TokenCacheFacts['entries'] = []
    let hasUser = false
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith(CACHE_PREFIX)) continue
      if (key.endsWith(USER_SUFFIX)) {
        hasUser = true
        continue
      }
      try {
        const wrapped = JSON.parse(localStorage.getItem(key) ?? 'null') as {
          body?: { access_token?: unknown; refresh_token?: unknown; oauthTokenScope?: unknown }
          expiresAt?: unknown
        } | null
        const body = wrapped?.body ?? {}
        entries.push({
          requestedScope: key.split('::')[3] ?? null,
          grantedScope: typeof body.oauthTokenScope === 'string' ? body.oauthTokenScope : null,
          hasAccessToken: typeof body.access_token === 'string',
          hasRefreshToken: typeof body.refresh_token === 'string',
          secondsToExpiry: typeof wrapped?.expiresAt === 'number' ? wrapped.expiresAt - now : null,
        })
      } catch {
        entries.push({
          requestedScope: key.split('::')[3] ?? null,
          grantedScope: null,
          hasAccessToken: false,
          hasRefreshToken: false,
          secondsToExpiry: null,
        })
      }
    }
    return { storageOk: true, hasUser, entries }
  } catch {
    return { storageOk: false, hasUser: false, entries: [] }
  }
}

/** Point auth telemetry somewhere, or pass null to send it nowhere again. */
// The cache as it was *before* anything failed. A `login_required` from the
// silent-auth iframe makes the SDK log out locally, which wipes the cache, so
// by the time any event is reported the live cache always reads empty. These
// two snapshots are what is left to go on.
let atLaunch: string | null = null
let beforeCall: string | null = null
let beforeCallFacts: TokenCacheFacts | null = null

export function setAuthTelemetry(fn: ((event: AuthEvent) => void) | null): void {
  sink = fn
  // Apps install the sink at module scope, before the Auth0 client exists, so
  // this is the cache exactly as the app found it on launch.
  if (fn && atLaunch === null) atLaunch = summariseCache(describeTokenCache())
}

/**
 * Remember the cache as it is right now, just before a token call that might
 * fail. Reported with every later event as `sdkCacheBeforeCall`.
 */
export function noteTokenCache(): void {
  try {
    beforeCallFacts = describeTokenCache()
    beforeCall = summariseCache(beforeCallFacts)
  } catch {
    // Diagnostics only.
  }
}

// The SDK renews any token within 60 seconds of expiry, so use the same margin.
const EXPIRY_LEEWAY_S = 60

/**
 * Whether the last `noteTokenCache()` found only access tokens that had
 * already run out.
 *
 * A token call that fails then means Auth0 refused to renew a session the
 * member had been away from: an ordinary end of session. A failure while the
 * cached token was still valid is different: that is how the iOS sign-in bug
 * looked (fixed in ae2cc4c), and it is worth a warning.
 */
export function cachedTokenHadExpired(): boolean {
  const entries = beforeCallFacts?.entries ?? []
  return (
    entries.length > 0 &&
    entries.every((e) => e.secondsToExpiry !== null && e.secondsToExpiry < EXPIRY_LEEWAY_S)
  )
}

function summariseCache(facts: TokenCacheFacts): string {
  if (!facts.storageOk) return 'storage unreadable'
  const user = `user=${facts.hasUser ? 'y' : 'n'}`
  if (facts.entries.length === 0) return `${user} no entries`
  return (
    `${user} ` +
    facts.entries
      .map(
        (e) =>
          `asked=[${e.requestedScope ?? '?'}] granted=[${e.grantedScope ?? '?'}] ` +
          `access=${e.hasAccessToken ? 'y' : 'n'} renew=${e.hasRefreshToken ? 'y' : 'n'} ` +
          `ttl=${e.secondsToExpiry ?? '?'}s`,
      )
      .join(' | ')
  )
}

/**
 * Report an auth event, if anything is listening.
 *
 * Reporting is diagnostics: it must never be the reason a sign-in fails, so a
 * sink that throws is swallowed rather than propagated to the caller.
 */
export function reportAuthEvent(
  name: string,
  data: Record<string, unknown> = {},
  level?: AuthEvent['level'],
): void {
  if (!sink) return
  try {
    // Named and flattened for Sentry: its server-side scrubber blanks any key
    // containing "token", "auth" or "session", and its default depth limit
    // would collapse the entry list to "[Object]". One string survives both.
    sink({
      name,
      data: {
        ...data,
        sdkCache: summariseCache(describeTokenCache()),
        ...(atLaunch !== null ? { sdkCacheAtLaunch: atLaunch } : null),
        ...(beforeCall !== null ? { sdkCacheBeforeCall: beforeCall } : null),
      },
      ...(level ? { level } : null),
    })
  } catch {
    // A broken reporter is not worth breaking the flow it was watching.
  }
}
